/**
 * Advanced Simulation Export Modal
 *
 * Modal for exporting advanced simulation runs in various formats.
 * Adapted from SimulationExportModal.js
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
  Chip
} from '@mui/material';
import {
  Download as DownloadIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import advancedSimulationApi from '../../../services/advancedSimulationApi';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 600 },
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  overflow: 'auto'
};

const AdvancedSimulationExportModal = ({ open, onClose, runId, token, run }) => {
  // State for export configuration
  const [config, setConfig] = useState({
    format: 'pdf',
    includeSystemPrompts: false,
    includeTranscript: true,
    includeSummary: true,
    includeReview: true,
    includeMetadata: true
  });

  // State for export
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  const handleConfigChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleExport = async () => {
    setDownloading(true);
    setError(null);

    try {
      let response;

      if (config.format === 'pdf') {
        response = await advancedSimulationApi.exportRunPDF(runId, config, token);
        const blob = new Blob([response], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `advanced-simulation-${runId}-${Date.now()}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else if (config.format === 'markdown') {
        response = await advancedSimulationApi.exportRunMarkdown(runId, token);
        const blob = new Blob([response], { type: 'text/markdown' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `advanced-simulation-${runId}-${Date.now()}.md`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else if (config.format === 'json') {
        response = await advancedSimulationApi.exportRunJSON(runId, token);
        const blob = new Blob([JSON.stringify(response, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `advanced-simulation-${runId}-${Date.now()}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else if (config.format === 'csv') {
        response = await advancedSimulationApi.exportRunCSV(runId, token);
        const blob = new Blob([response], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `advanced-simulation-${runId}-${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }

      // Close modal after successful download
      onClose();
    } catch (error) {
      console.error('Error exporting simulation:', error);
      setError('Failed to export simulation. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  // Extract metadata from run
  const hasSummary = run?.aiSummary?.therapistPerspective || run?.aiSummary?.clientPerspective;
  const hasReview = run?.review?.evaluation;
  const turnCount = run?.turns?.length || 0;
  const clientPersona = run?.configSnapshot?.clientPersonaKey || 'Unknown';
  const therapistPersona = run?.configSnapshot?.therapistPersonaKey || 'Unknown';

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Typography variant="h5" component="h2" gutterBottom>
          Export Advanced Simulation
        </Typography>

        {run && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                icon={<CheckCircleIcon />}
                label={`${turnCount} turns`}
                size="small"
                color="primary"
              />
              <Chip
                label={`Client: ${clientPersona}`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`Therapist: ${therapistPersona}`}
                size="small"
                variant="outlined"
              />
            </Stack>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={3}>
          {/* Format Selection */}
          <FormControl fullWidth>
            <InputLabel>Export Format</InputLabel>
            <Select
              value={config.format}
              label="Export Format"
              onChange={handleConfigChange('format')}
            >
              <MenuItem value="pdf">PDF Document</MenuItem>
              <MenuItem value="markdown">Markdown</MenuItem>
              <MenuItem value="json">JSON</MenuItem>
              <MenuItem value="csv">CSV (Transcript Only)</MenuItem>
            </Select>
          </FormControl>

          <Divider />

          {/* Content Options */}
          <Typography variant="subtitle1" fontWeight="bold">
            Content to Include
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={config.includeTranscript}
                onChange={handleConfigChange('includeTranscript')}
                disabled
              />
            }
            label="Transcript (always included)"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={config.includeSystemPrompts}
                onChange={handleConfigChange('includeSystemPrompts')}
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                System Prompts
                <Typography variant="caption" color="text.secondary">
                  (for analysis purposes)
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={config.includeSummary}
                onChange={handleConfigChange('includeSummary')}
                disabled={!hasSummary}
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                Summary
                {!hasSummary && (
                  <Chip
                    icon={<WarningIcon />}
                    label="Not yet generated"
                    size="small"
                    color="warning"
                  />
                )}
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={config.includeReview}
                onChange={handleConfigChange('includeReview')}
                disabled={!hasReview}
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                Review
                {!hasReview && (
                  <Chip
                    icon={<WarningIcon />}
                    label="Not yet generated"
                    size="small"
                    color="warning"
                  />
                )}
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={config.includeMetadata}
                onChange={handleConfigChange('includeMetadata')}
              />
            }
            label="Metadata (timestamps, configuration, metrics)"
          />

          <Divider />

          {/* Info Alert */}
          {config.includeSystemPrompts && (
            <Alert severity="info" icon={<InfoIcon />}>
              System prompts will be included in the export. These contain internal configuration details.
            </Alert>
          )}

          {/* Action Buttons */}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={onClose} disabled={downloading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleExport}
              disabled={downloading}
              startIcon={downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
            >
              {downloading ? 'Exporting...' : 'Export'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
};

export default AdvancedSimulationExportModal;
